import { Business as BusinessIcon, Key as KeyIcon, Person as PersonIcon } from '@mui/icons-material';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React from 'react';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { useDirection } from '../context/DirectionContext';
import { translate as tr } from '../i18n/translations';
import apiService from '../services/api';

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      py: 2,
      borderBottom: '1px solid #F1F5F9',
      '&:last-child': { borderBottom: 'none' },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        bgcolor: '#EEF2FF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6366F1',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0F172A', fontFamily: 'monospace' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const Units: React.FC = () => {
  const { lang } = useDirection();
  const { data: unitInfo, isLoading, error } = useQuery({
    queryKey: ['unitInfo'],
    queryFn: () => apiService.getUnitInfo(),
  });

  if (isLoading) return <LoadingSpinner message={tr('units.loading', lang)} />;

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <Box>
        <Typography
          sx={{ color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}
        >
          {tr('units.section', lang)}
        </Typography>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {tr('common.mgmt', lang)}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {(error as any).message}
          </Alert>
        )}

        {unitInfo ? (
          <Box sx={{ maxWidth: 560 }}>
            <Card>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F1F5F9' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{tr('units.currentUnit', lang)}</Typography>
                </Box>
                <Box sx={{ px: 3 }}>
                  <InfoRow
                    icon={<BusinessIcon fontSize="small" />}
                    label={tr('units.unitId', lang)}
                    value={unitInfo.unitId}
                  />
                  <InfoRow
                    icon={<PersonIcon fontSize="small" />}
                    label={tr('units.userId', lang)}
                    value={unitInfo.userId}
                  />
                  <InfoRow
                    icon={<KeyIcon fontSize="small" />}
                    label={tr('common.email', lang)}
                    value={unitInfo.email}
                  />
                </Box>
              </CardContent>
            </Card>

            {unitInfo.message && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {unitInfo.message}
              </Alert>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            {tr('units.noInfo', lang)}
          </Alert>
        )}
      </Box>
    </motion.div>
  );
};

export default Units;
